import { useState } from 'react'

import { AvailableModules, Features } from 'generated/featureDefinitions'

import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'

import subscriptions from 'modules/billing/store/collections/subscriptions'
import { useItemProvider } from 'modules/processes/components/ItemWizard/Providers/item'
import ItemCard from 'modules/processes/components/ItemWizard/components/ItemCard'
import { SelectorProps } from 'modules/processes/lib/types'
import { useProcessContext } from 'modules/processes/pages/Process/ProcessRootPage'
import RequestUpgradeModal from 'modules/upsell/components/RequestUpgradeModal'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'

enum TrackData {
  from = 'workflow_survey_action_configuration',
}

export const Selector = ({ title, description, identifier, onSelect }: SelectorProps) => {
  const { itemType } = useItemProvider()
  const { process } = useProcessContext()
  const [openUpgradeModal, setOpenUpgradeModal] = useState<boolean>(false)
  const subscription = subscriptions.current
  const currentCompanyId = companies.current.id

  const clickButton = () => {
    track(trackingEvents.processes.upgradePlanOpened, {
      from: TrackData.from,
      company_id: currentCompanyId,
    })
  }

  const contactSuccess = () => {
    track(trackingEvents.processes.upgradePlanContactSuccessful, {
      from: TrackData.from,
      company_id: currentCompanyId,
    })
  }

  const handleOpenUpgradeModal = () => {
    setOpenUpgradeModal(true)
    clickButton()
  }

  const { featureAvailability, loading } = useFeatureAvailability(Features.surveys_core)

  const selectorTitle = featureAvailability.available
    ? title
    : i18n.t('processes.process_settings.actions.survey_action.request_update.title')
  const selectorDescription = featureAvailability.available
    ? description
    : i18n.t('processes.process_settings.actions.survey_action.request_update.description', {
        workflow: process?.name,
      })

  if (loading) return null

  return (
    <>
      <ItemCard
        title={selectorTitle}
        description={selectorDescription}
        onSelect={() => onSelect(identifier)}
        itemType={identifier}
        value={itemType}
        isUpgradeSelector={!featureAvailability.available}
        openUpgradeModal={handleOpenUpgradeModal}
      />
      {openUpgradeModal && (
        <RequestUpgradeModal
          requestReason={
            subscription.isLegacy ? ValidReasons.MissingFeatureLegacy : ValidReasons.MissingFeature
          }
          featureRequested={Features.surveys_core}
          requestSource="Workflow survey action configuration"
          title={i18n.t('processes.process_settings.actions.survey_action.request_update.title')}
          description={i18n.t(
            'processes.process_settings.actions.survey_action.request_update.description',
            { workflow: process?.name }
          )}
          moduleRequested={AvailableModules.surveys}
          onClose={() => setOpenUpgradeModal(false)}
          onSuccess={() => contactSuccess()}
        />
      )}
    </>
  )
}
